/**
 * Shared homeowner intake rules.
 * Used by the CSV preview in the browser and by Netlify functions, so the
 * two sides cannot drift. No React.
 *
 * Canonical feature ids are defined here. Existing task-library names are
 * accepted as aliases and stored as the canonical id. This file does not
 * change src/utils/taskAssignment.js.
 */

import { planHomeownerLimit } from './planLimits';
import { homeProfileColumns } from './homeProfile';

const CANONICAL_FEATURES = [
  'pool',
  'spa',
  'irrigation',
  'well',
  'septic',
  'solar_panels',
  'generator',
  'screen_enclosure',
  'hurricane_shutters',
  'impact_windows',
  'security_system',
  'smart_home',
  'outdoor_kitchen',
  'fireplace',
  'ev_charger',
  'water_softener',
  'whole_house_filter',
  'garage',
  'sump_pump',
  'gutters',
  'fence',
  'surge_protector',
];

const FEATURE_LABELS = {
  pool: 'Swimming Pool',
  spa: 'Hot Tub/Spa',
  irrigation: 'Irrigation System',
  well: 'Well Water',
  septic: 'Septic System',
  solar_panels: 'Solar Panels',
  generator: 'Whole Home Generator',
  screen_enclosure: 'Screen Enclosure (Pool/Lanai)',
  hurricane_shutters: 'Storm shutters',
  impact_windows: 'Impact Windows',
  security_system: 'Security System',
  smart_home: 'Smart Home System',
  outdoor_kitchen: 'Outdoor Kitchen',
  fireplace: 'Fireplace/Chimney',
  ev_charger: 'EV Charging Station',
  water_softener: 'Water Softener',
  whole_house_filter: 'Whole House Water Filter',
  garage: 'Garage',
  sump_pump: 'Sump Pump',
  gutters: 'Gutters',
  fence: 'Fence',
  surge_protector: 'Whole-House Surge Protector',
};

/** Lowercased, separator-insensitive alias → canonical id. */
const FEATURE_ALIASES = {
  pool: 'pool',
  swimming_pool: 'pool',
  spa: 'spa',
  hot_tub: 'spa',
  hottub: 'spa',
  irrigation: 'irrigation',
  lawn_irrigation: 'irrigation',
  sprinkler: 'irrigation',
  sprinklers: 'irrigation',
  well: 'well',
  well_water: 'well',
  septic: 'septic',
  solar: 'solar_panels',
  solar_panel: 'solar_panels',
  solar_panels: 'solar_panels',
  generator: 'generator',
  screen_enclosure: 'screen_enclosure',
  hurricane_shutters: 'hurricane_shutters',
  hurricaneshutters: 'hurricane_shutters',
  impact_windows: 'impact_windows',
  impactwindows: 'impact_windows',
  security_system: 'security_system',
  security: 'security_system',
  smart_home: 'smart_home',
  outdoor_kitchen: 'outdoor_kitchen',
  fireplace: 'fireplace',
  ev_charger: 'ev_charger',
  water_softener: 'water_softener',
  whole_house_filter: 'whole_house_filter',
  garage: 'garage',
  garagedoor: 'garage',
  garage_door: 'garage',
  sump_pump: 'sump_pump',
  sumppump: 'sump_pump',
  gutters: 'gutters',
  gutter: 'gutters',
  fence: 'fence',
  surge_protector: 'surge_protector',
  wholehousesurgeprotector: 'surge_protector',
  whole_house_surge_protector: 'surge_protector',
  watertreatmentsystem: 'whole_house_filter',
  water_treatment_system: 'whole_house_filter',
  solarelectric: 'solar_panels',
  solar_electric: 'solar_panels',
  solarwaterheater: 'solar_panels',
  solar_water_heater: 'solar_panels',
  hurricane: 'hurricane_shutters',
  screen: 'screen_enclosure',
  screenenclosure: 'screen_enclosure',
};

const PROPERTY_TYPES = ['single_family', 'condo', 'townhome', 'mobile_home'];

const PROPERTY_TYPE_ALIASES = {
  single_family: 'single_family',
  singlefamily: 'single_family',
  sfr: 'single_family',
  house: 'single_family',
  home: 'single_family',
  condo: 'condo',
  condominium: 'condo',
  townhome: 'townhome',
  townhouse: 'townhome',
  town_house: 'townhome',
  mobile_home: 'mobile_home',
  mobilehome: 'mobile_home',
  manufactured: 'mobile_home',
  manufactured_home: 'mobile_home',
  multi_family: 'mobile_home',
  multifamily: 'mobile_home',
  apartment: 'condo',
  apartments: 'condo',
};

const PROPERTY_TYPE_LABELS = {
  single_family: 'Single Family Home',
  condo: 'Condo',
  townhome: 'Townhome',
  mobile_home: 'Mobile Home',
};

function propertyTypeChoices() {
  return PROPERTY_TYPES.map((id) => ({
    id,
    value: id,
    label: PROPERTY_TYPE_LABELS[id],
  }));
}

const USPS_STATES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC',
]);

const PLACEHOLDER_DOMAINS = new Set(['example.com', 'test.com']);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FEATURE_OPTIONS = CANONICAL_FEATURES.map((id) => ({
  id,
  label: FEATURE_LABELS[id],
}));

function aliasKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_');
}

function canonicalFeature(value) {
  if (value == null || String(value).trim() === '') return null;
  return FEATURE_ALIASES[aliasKey(value)] || null;
}

function canonicalPropertyType(value) {
  if (value == null || String(value).trim() === '') return null;
  return PROPERTY_TYPE_ALIASES[aliasKey(value)] || null;
}

function featureSelected(list, id) {
  const wanted = canonicalFeature(id);
  if (!wanted) return false;
  return (Array.isArray(list) ? list : []).some((item) => canonicalFeature(item) === wanted);
}

function toggleFeature(list, id) {
  const wanted = canonicalFeature(id);
  if (!wanted) return Array.isArray(list) ? list.slice() : [];
  const rest = normalizeFeatureList(list || []).features.filter((item) => item !== wanted);
  return featureSelected(list, id) ? rest : rest.concat(wanted);
}

function systemHasAnswer(system) {
  if (!system || typeof system !== 'object') return false;
  return Object.values(system).some((value) => value != null && String(value).trim() !== '');
}

function featuresFromHomeAnswers({ features, systems, sewerType, waterSource } = {}) {
  const next = new Set(normalizeFeatureList(features || []).features);
  if (systemHasAnswer(systems && systems.pool_equipment)) next.add('pool');
  if (systemHasAnswer(systems && systems.septic_system) || aliasKey(sewerType) === 'septic') next.add('septic');
  if (systemHasAnswer(systems && systems.well_pump) || aliasKey(waterSource).indexOf('well') !== -1) next.add('well');
  if (systemHasAnswer(systems && systems.solar_panels)) next.add('solar_panels');
  if (featureSelected(features, 'hurricane_shutters')) next.add('hurricane_shutters');
  return Array.from(next);
}

function normalizeFeatureList(input) {
  const warnings = [];
  const seen = new Set();
  const features = [];
  const rawList = Array.isArray(input)
    ? input
    : String(input || '')
      .split(/[|;]/)
      .map((part) => part.trim())
      .filter(Boolean);

  rawList.forEach((item) => {
    const canonical = canonicalFeature(item);
    if (!canonical) {
      warnings.push(`Unknown feature "${String(item).trim()}" was dropped`);
      return;
    }
    if (!seen.has(canonical)) {
      seen.add(canonical);
      features.push(canonical);
    }
  });

  return { features, warnings };
}

function blankToNull(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
}

function normalizePhone(value) {
  const original = blankToNull(value);
  if (!original) return { value: null };
  const digits = original.replace(/\D/g, '');
  if (digits.length === 10) return { value: `+1${digits}` };
  if (original.length > 30) return { error: 'Must be 30 characters or fewer' };
  return { value: original };
}

function isPlaceholderEmail(email) {
  const domain = email.split('@')[1] || '';
  if (PLACEHOLDER_DOMAINS.has(domain)) return true;
  return [...PLACEHOLDER_DOMAINS].some((blocked) => domain.endsWith(`.${blocked}`));
}

/**
 * Validate and normalize one homeowner record.
 * @returns {{ ok: boolean, data: object|null, errors: object, warnings: string[] }}
 */
function normalizeHomeowner(input, options = {}) {
  const source = input && typeof input === 'object' ? input : {};
  const errors = {};
  const warnings = [];
  const nowYear = options.nowYear || new Date().getFullYear();

  const firstName = blankToNull(source.first_name);
  const lastName = blankToNull(source.last_name);
  const emailRaw = blankToNull(source.email);

  if (!firstName) errors.first_name = 'First name is required';
  else if (firstName.length > 100) errors.first_name = 'Must be 100 characters or fewer';

  if (!lastName) errors.last_name = 'Last name is required';
  else if (lastName.length > 100) errors.last_name = 'Must be 100 characters or fewer';

  let email = null;
  if (!emailRaw) errors.email = 'Email is required';
  else {
    email = emailRaw.toLowerCase();
    if (email.length > 254) errors.email = 'Must be 254 characters or fewer';
    else if (!EMAIL_RE.test(email)) errors.email = 'Enter a valid email address';
    else if (isPlaceholderEmail(email)) errors.email = 'Use a real email address';
  }

  const phoneResult = normalizePhone(source.phone);
  if (phoneResult.error) errors.phone = phoneResult.error;

  const address = blankToNull(source.address);
  if (address && address.length > 200) errors.address = 'Must be 200 characters or fewer';

  const city = blankToNull(source.city);
  if (city && city.length > 100) errors.city = 'Must be 100 characters or fewer';

  let state = null;
  const stateRaw = blankToNull(source.state);
  if (stateRaw) {
    state = stateRaw.toUpperCase();
    if (!USPS_STATES.has(state)) errors.state = 'Use a 2-letter USPS state code';
  }

  let zip = null;
  const zipRaw = blankToNull(source.zip_code != null ? source.zip_code : source.zip);
  if (zipRaw) {
    zip = zipRaw;
    if (!/^\d{5}(-\d{4})?$/.test(zip)) errors.zip_code = 'Use a 5-digit or ZIP+4 code';
  }

  const countyFips = blankToNull(source.property_county_fips);
  if (countyFips && !/^\d{5}$/.test(countyFips)) errors.property_county_fips = 'Choose a county';
  const countySource = source.property_county_source === 'picked' ? 'picked' : (countyFips ? 'zip' : null);

  let propertyType = 'single_family';
  const propertyRaw = blankToNull(source.property_type);
  if (propertyRaw) {
    const canonicalType = canonicalPropertyType(propertyRaw);
    if (!canonicalType) errors.property_type = 'Unknown property type';
    else propertyType = canonicalType;
  }

  const featureResult = normalizeFeatureList(source.features);
  warnings.push(...featureResult.warnings);

  let yearBuilt = null;
  const yearRaw = blankToNull(source.year_built);
  if (yearRaw != null) {
    if (!/^\d{4}$/.test(yearRaw)) errors.year_built = `Use a year from 1700 to ${nowYear + 1}`;
    else {
      yearBuilt = Number(yearRaw);
      if (yearBuilt < 1700 || yearBuilt > nowYear + 1) {
        errors.year_built = `Use a year from 1700 to ${nowYear + 1}`;
      }
    }
  }

  let squareFeet = null;
  const sqRaw = blankToNull(source.square_feet);
  if (sqRaw != null) {
    if (!/^\d+$/.test(sqRaw)) errors.square_feet = 'Use a whole number from 100 to 100000';
    else {
      squareFeet = Number(sqRaw);
      if (squareFeet < 100 || squareFeet > 100000) {
        errors.square_feet = 'Use a whole number from 100 to 100000';
      }
    }
  }

  const notes = blankToNull(source.notes);
  if (notes && notes.length > 2000) errors.notes = 'Must be 2000 characters or fewer';

  const profile = homeProfileColumns(source, nowYear);
  Object.assign(errors, profile.errors);
  warnings.push(...profile.warnings);

  const externalId = blankToNull(source.external_id);
  if (externalId && externalId.length > 128) errors.external_id = 'Must be 128 characters or fewer';

  const data = {
    first_name: firstName,
    last_name: lastName,
    email,
    phone: phoneResult.value,
    address,
    city,
    state,
    zip_code: zip,
    property_county_fips: countyFips,
    property_county_source: countySource,
    property_type: propertyType,
    features: featureResult.features,
    year_built: yearBuilt,
    square_feet: squareFeet,
    notes,
    external_id: externalId,
  };
  Object.assign(data, profile.patch);

  return {
    ok: Object.keys(errors).length === 0,
    data: Object.keys(errors).length === 0 ? data : null,
    errors,
    warnings,
  };
}

function splitFullName(value) {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0], last_name: '' };
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
}

function intakeBlockReason(profile) {
  if (!profile) return 'agent_not_found';
  if (profile.role === 'super_admin') return null;
  const status = profile.subscription_status || '';
  if (status === 'trialing' || status === 'active') return null;
  return 'subscription_inactive';
}

function homeownerLimit(profile, team) {
  return planHomeownerLimit(profile, team);
}

function inviteDailyCap(profile) {
  if (profile && profile.subscription_status === 'trialing') return 50;
  const configured = parseInt(process.env.INVITE_DAILY_CAP_PER_AGENT || '', 10);
  if (Number.isFinite(configured) && configured > 0) return configured;
  return 200;
}

const MANUAL_PROPERTY_FIELDS = [
  'construction_type',
  'exterior_finish',
  'foundation_type',
  'roof_material',
  'property_photo_url',
  'hvac_count',
  'hvac_install_years',
  'hvac_types',
  'roof_type',
  'roof_install_year',
  'roof_year',
  'water_heater_type',
  'water_heater_install_year',
  'water_source',
  'irrigation_source',
  'well_pump_install_year',
  'sewer_type',
  'septic_install_year',
  'pool_pump_install_year',
  'pool_heater_install_year',
  'lot_size',
  'is_team_client',
  'filter_type',
  'hvac_filter_sizes',
];

function pickPropertyDetails(input) {
  if (!input || typeof input !== 'object') return {};
  const patch = {};
  MANUAL_PROPERTY_FIELDS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(input, key)) {
      patch[key] = input[key];
    }
  });
  const profile = homeProfileColumns(input);
  Object.keys(profile.patch).forEach((key) => {
    patch[key] = profile.patch[key];
  });
  Object.keys(profile.errors).forEach((key) => {
    delete patch[key];
    if (key === 'roof_year') delete patch.roof_install_year;
  });
  return patch;
}

export {
  CANONICAL_FEATURES,
  FEATURE_LABELS,
  FEATURE_OPTIONS,
  FEATURE_ALIASES,
  PROPERTY_TYPES,
  PROPERTY_TYPE_LABELS,
  propertyTypeChoices,
  canonicalFeature,
  canonicalPropertyType,
  normalizeFeatureList,
  featureSelected,
  toggleFeature,
  featuresFromHomeAnswers,
  normalizeHomeowner,
  splitFullName,
  intakeBlockReason,
  homeownerLimit,
  inviteDailyCap,
  pickPropertyDetails,
};
